import { useEffect, useState, type FormEvent } from 'react';
import type { CompanyUpdate } from '../types';
import { GlobalButton } from './GlobalButton';
import { GlobalInput } from './GlobalInput';
import { GlobalTextarea } from './GlobalTextarea';

type Props = {
  update?: CompanyUpdate | null;
  companyId: string;
  onSubmit: (next: CompanyUpdate) => void;
  onCancel: () => void;
  onDelete?: () => void;
};

function toDateInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export function CompanyUpdateForm({
  update,
  companyId,
  onSubmit,
  onCancel,
  onDelete,
}: Props) {
  const [dateStr, setDateStr] = useState(() =>
    update ? toDateInput(update.date) : toDateInput(new Date().toISOString())
  );
  const [rawText, setRawText] = useState(() => update?.rawText ?? '');

  useEffect(() => {
    setDateStr(update ? toDateInput(update.date) : toDateInput(new Date().toISOString()));
    setRawText(update?.rawText ?? '');
  }, [update]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const dateIso = dateStr
      ? new Date(`${dateStr}T12:00:00`).toISOString()
      : new Date().toISOString();
    const now = new Date().toISOString();

    const base = update
      ? update
      : ({
          id: '',
          companyId,
          date: dateIso,
          rawText: '',
          aiSummary: '',
          sentiment: 'neutral',
          keyPoints: [],
          createdAt: now,
        } as CompanyUpdate);

    onSubmit({
      ...base,
      companyId,
      date: dateIso,
      rawText,
      id: update?.id ?? base.id,
      createdAt: update?.createdAt ?? now,
    });
  }

  return (
    <form className="sidebarFormStack" onSubmit={handleSubmit}>
      <GlobalInput
        label="Note date"
        type="date"
        value={dateStr}
        onChange={(e) => setDateStr(e.target.value)}
        required
      />
      <GlobalTextarea
        label="What changed"
        rows={8}
        value={rawText}
        onChange={(e) => setRawText(e.target.value)}
        required
        placeholder=" "
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'space-between', marginTop: 8 }}>
        <div>
          {onDelete ? (
            <GlobalButton type="button" variant="secondary" destructive onClick={onDelete}>
              Delete note
            </GlobalButton>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <GlobalButton type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </GlobalButton>
          <GlobalButton type="submit" variant="primary">
            Save
          </GlobalButton>
        </div>
      </div>
    </form>
  );
}
